// Reusable label engine (spec-driven) shared by the label stations.
// A label is described as elements in printer-dot coordinates and rendered both to
// a WYSIWYG preview (LabelCanvas) and to ZPL (specToZpl) from the SAME spec, so the
// print matches the preview. Label size (labelW × labelH, in dots @203dpi) is passed
// per label — drum/flange = 812×609 (100×75mm), pallet = 800×1200 (100×150mm).
import logoFinal from './logo_final.jpg';
import ceMarking from './ce-marking-logo.jpg';

export { logoFinal, ceMarking };

// cosmetic faux-QR for the preview (the real QR is printed by the Zebra ^BQ)
export function FauxQR({ payload = '', px = 100 }) {
  const N = 21;
  const cells = [];
  let h = 2166136261;
  for (let i = 0; i < payload.length; i++) { h ^= payload.charCodeAt(i); h = Math.imul(h, 16777619); }
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
    const inFinder = (r < 7 && c < 7) || (r < 7 && c >= N - 7) || (r >= N - 7 && c < 7);
    let on;
    if (inFinder) {
      const oR = r < 7 ? 0 : N - 7, oC = c < 7 ? 0 : N - 7;
      const m = Math.max(Math.abs(r - oR - 3), Math.abs(c - oC - 3));
      on = m === 3 || m <= 1;
    } else {
      let x = Math.imul((h ^ (r * 131 + c * 977)) >>> 0, 2654435761); x ^= x >>> 15;
      on = ((x >>> 0) % 100) > 52;
    }
    cells.push(on);
  }
  return (
    <div className="lbl-qr" style={{ width: px, height: px, gridTemplateColumns: `repeat(${N},1fr)`, gridTemplateRows: `repeat(${N},1fr)` }}>
      {cells.map((on, i) => <span key={i} style={{ background: on ? '#000' : '#fff' }} />)}
    </div>
  );
}

// render a spec (elements in label-dot coords) scaled into a widthPx-wide preview
export function LabelCanvas({ elements, labelW = 812, labelH = 609, widthPx = 360 }) {
  const scale = widthPx / labelW;
  return (
    <div className="lbl-canvas" style={{ width: widthPx, height: labelH * scale }}>
      {elements.map((el, i) => {
        const base = { position: 'absolute', left: el.x * scale, top: el.y * scale };
        if (el.type === 'line')
          return <div key={i} style={{ ...base, width: (el.w || labelW) * scale, height: Math.max(1, (el.h || 2) * scale), background: '#000' }} />;
        if (el.type === 'box')
          return <div key={i} style={{ ...base, width: el.w * scale, height: el.h * scale, border: `${Math.max(1, (el.t || 2) * scale)}px solid #000`, boxSizing: 'border-box' }} />;
        if (el.type === 'qr')
          return <div key={i} style={base}><FauxQR payload={el.payload} px={el.s * scale} /></div>;
        if (el.type === 'logo')
          return el.src
            ? <img key={i} src={el.src} alt="logo" style={{ ...base, width: el.w * scale, height: el.h * scale, objectFit: 'contain' }} />
            : <div key={i} className="lbl-logo-ph" style={{ ...base, width: el.w * scale, height: el.h * scale, fontSize: el.h * 0.5 * scale }}>{el.text || 'LOGO'}</div>;
        return (
          <div key={i} style={{ ...base, width: el.w ? el.w * scale : undefined, fontSize: (el.size || 20) * scale, lineHeight: 1, fontWeight: el.bold ? 700 : 400, fontFamily: 'monospace', color: '#000', whiteSpace: 'nowrap', textAlign: el.align || 'left',
            // A div given a width must not draw outside it — otherwise a long
            // product name runs under the figures sharing its line and the
            // preview disagrees with the paper.
            overflow: el.w ? 'hidden' : undefined }}>
            {el.text}
          </div>
        );
      })}
    </div>
  );
}

export const zesc = s => String(s ?? '').replace(/[\^~]/g, '');

// spec → ZPL (label size in dots). Print matches the preview.
export function specToZpl(elements, { labelW = 812, labelH = 609 } = {}) {
  let z = `^XA\n^CI28\n^PW${Math.round(labelW)}\n^LL${Math.round(labelH)}\n~SD20\n`;
  for (const el of elements) {
    const x = Math.round(el.x), y = Math.round(el.y);
    if (el.type === 'text') {
      const s = Math.round(el.size || 20);
      if (el.align === 'center' && el.w) {
        z += `^FO${x},${y}^A0N,${s},${s}^FB${Math.round(el.w)},1,0,C^FD${zesc(el.text)}^FS\n`;
      } else {
        z += `^FO${x},${y}^A0N,${s},${s}^FD${zesc(el.text)}^FS\n`;
      }
    } else if (el.type === 'line') {
      const w = Math.max(1, Math.round(el.w || labelW)), h = Math.max(1, Math.round(el.h || 2));
      z += `^FO${x},${y}^GB${w},${h},${Math.max(1, Math.min(w, h))}^FS\n`;
    } else if (el.type === 'box') {
      z += `^FO${x},${y}^GB${Math.round(el.w)},${Math.round(el.h)},${Math.round(el.t || 2)}^FS\n`;
    } else if (el.type === 'qr') {
      const mag = el.mag || Math.max(2, Math.min(10, Math.round((el.s || 150) / 33)));
      z += `^FO${x},${y}^BQN,2,${mag}^FDMA,${zesc(el.payload)}^FS\n`;
    } else if (el.type === 'logo' && el.gfa) {
      z += `^FO${x},${y}${el.gfa}^FS\n`;
    } else if (el.type === 'logo' && el.text) {
      const s = Math.round(el.h * 0.5);
      z += `^FO${x},${y}^A0N,${s},${s}^FD${zesc(el.text)}^FS\n`;
    }
  }
  return z + '\n^XZ';
}

// convert an <img> to a monochrome ZPL ^GFA graphic (threshold, 1=black)
export function imageToGfa(img, widthDots, threshold = 0.68) {
  const w = Math.max(1, Math.round(widthDots));
  const h = Math.max(1, Math.round(w * (img.naturalHeight / img.naturalWidth)));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const bytesPerRow = Math.ceil(w / 8);
  const total = bytesPerRow * h;
  let hex = '';
  for (let py = 0; py < h; py++) {
    for (let bx = 0; bx < bytesPerRow; bx++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const px = bx * 8 + bit;
        let black = 0;
        if (px < w) {
          const idx = (py * w + px) * 4;
          const a = data[idx + 3];
          const lum = a < 128 ? 1 : (0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]) / 255;
          black = lum < threshold ? 1 : 0;
        }
        byte = (byte << 1) | black;
      }
      hex += byte.toString(16).padStart(2, '0').toUpperCase();
    }
  }
  return { gfa: `^GFA,${total},${total},${bytesPerRow},${hex}`, w, h };
}

// 1-bit monochrome ZPL ^GFA from a <canvas>
export function canvasToGfa(canvas, threshold = 0.68) {
  const w = canvas.width, h = canvas.height;
  const { data } = canvas.getContext('2d').getImageData(0, 0, w, h);
  const bytesPerRow = Math.ceil(w / 8);
  const total = bytesPerRow * h;
  let hex = '';
  for (let py = 0; py < h; py++) {
    for (let bx = 0; bx < bytesPerRow; bx++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const px = bx * 8 + bit;
        let black = 0;
        if (px < w) {
          const idx = (py * w + px) * 4;
          const a = data[idx + 3];
          const lum = a < 128 ? 1 : (0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]) / 255;
          black = lum < threshold ? 1 : 0;
        }
        byte = (byte << 1) | black;
      }
      hex += byte.toString(16).padStart(2, '0').toUpperCase();
    }
  }
  return { gfa: `^GFA,${total},${total},${bytesPerRow},${hex}`, w, h };
}

// draw a spec's elements onto a 2D canvas context (QR handled separately as native ^BQ)
export function renderSpecToCanvas(ctx, elements, images = {}) {
  for (const el of elements) {
    if (el.type === 'line') {
      ctx.fillStyle = '#000';
      ctx.fillRect(el.x, el.y, Math.max(1, el.w || 0), Math.max(1, el.h || 2));
    } else if (el.type === 'box') {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = el.t || 2;
      ctx.strokeRect(el.x + (el.t || 2) / 2, el.y + (el.t || 2) / 2, el.w - (el.t || 2), el.h - (el.t || 2));
    } else if (el.type === 'logo' && images[el.src]) {
      try { ctx.drawImage(images[el.src], el.x, el.y, el.w, el.h); } catch (_) { /* not loaded */ }
    } else if (el.type === 'logo' && el.text) {
      ctx.fillStyle = '#000';
      ctx.font = `bold ${Math.round(el.h * 0.5)}px monospace`;
      ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
      ctx.fillText(el.text, el.x + el.w / 2, el.y + el.h / 2);
    } else if (el.type === 'text') {
      ctx.fillStyle = '#000';
      ctx.font = `${el.bold ? 'bold ' : ''}${el.size || 20}px monospace`;
      ctx.textBaseline = 'top';
      // `right` used to fall through to `left`, so a right-aligned figure was
      // PRINTED at the left edge of its box — on top of whatever shared the
      // line. The preview is DOM and honours CSS text-align, so it looked
      // correct and only the paper was wrong.
      ctx.textAlign = el.align === 'center' ? 'center' : el.align === 'right' ? 'right' : 'left';
      const tx =
        el.align === 'center' && el.w ? el.x + el.w / 2
        : el.align === 'right' && el.w ? el.x + el.w
        : el.x;
      // `w` doubles as a hard bound: a string longer than its column is
      // condensed to fit rather than allowed to run into the next one.
      if (el.w) ctx.fillText(String(el.text ?? ''), tx, el.y, el.w);
      else ctx.fillText(String(el.text ?? ''), tx, el.y);
    }
  }
}

// Build a 90°-rotated label ZPL: the whole design (authored labelW×labelH landscape)
// is rasterized and rotated 90° CW onto portrait media (^PW=labelH, ^LL=labelW) as one
// ^GFA, so it fits a 100mm-wide printer while reading along the length. QR stays a
// native ^BQ (rotation-invariant) placed at the transformed position.
export function buildRotatedPalletZpl(elements, { labelW, labelH, images = {}, threshold = 0.68 } = {}) {
  const reading = document.createElement('canvas');
  reading.width = labelW; reading.height = labelH;
  const rctx = reading.getContext('2d');
  rctx.fillStyle = '#fff'; rctx.fillRect(0, 0, labelW, labelH);
  renderSpecToCanvas(rctx, elements.filter(e => e.type !== 'qr'), images);

  // portrait physical canvas, reading rotated 90° CW
  const phys = document.createElement('canvas');
  phys.width = labelH; phys.height = labelW;
  const pctx = phys.getContext('2d');
  pctx.fillStyle = '#fff'; pctx.fillRect(0, 0, phys.width, phys.height);
  pctx.translate(phys.width, 0);
  pctx.rotate(Math.PI / 2);
  pctx.drawImage(reading, 0, 0);

  const { gfa } = canvasToGfa(phys, threshold);
  let z = `^XA\n^CI28\n^PW${phys.width}\n^LL${phys.height}\n~SD20\n^FO0,0${gfa}^FS\n`;

  const qr = elements.find(e => e.type === 'qr');
  if (qr) {
    // reading (x,y) → physical (labelH - y, x); use the square's rotated top-left
    const physX = Math.round(labelH - qr.y - qr.s);
    const physY = Math.round(qr.x);
    const mag = qr.mag || 6;
    z += `^FO${physX},${physY}^BQN,2,${mag}^FDMA,${zesc(qr.payload)}^FS\n`;
  }
  return z + '\n^XZ';
}
